import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import { db } from '../firebase/config';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount || 0));

export default function PublicContract() {
  const { contractId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contract, setContract] = useState(null);
  const [client, setClient] = useState(null);
  const [billingContactEmail, setBillingContactEmail] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        setBillingContactEmail('');
        const ref = doc(db, 'contracts', contractId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setError('Contract not found.');
          setLoading(false);
          return;
        }
        const data = snap.data();
        if (!data.shareEnabled || !data.shareToken || data.shareToken !== token) {
          setError('This share link is invalid or disabled.');
          setLoading(false);
          return;
        }
        setContract({ id: snap.id, ...data });

        if (data.clientEntityId) {
          try {
            const clientRef = doc(db, 'clients', data.clientEntityId);
            const clientSnap = await getDoc(clientRef);
            if (clientSnap.exists()) setClient({ id: clientSnap.id, ...clientSnap.data() });
          } catch {}
          try {
            let contactsSnap = await getDocs(query(collection(db, 'contacts'), where('associatedClient', '==', data.clientEntityId)));
            if (contactsSnap.empty) {
              contactsSnap = await getDocs(query(collection(db, 'contacts'), where('clientId', '==', data.clientEntityId)));
            }
            const billingContact = contactsSnap.docs
              .map((contactDoc) => ({ id: contactDoc.id, ...contactDoc.data() }))
              .find((contact) => (
                (contact.isPrimaryBillingContact === true || contact.isPrimary === true) &&
                String(contact.email || '').trim()
              ));
            setBillingContactEmail(String(billingContact?.email || '').trim());
          } catch {}
        }
      } catch (e) {
        setError('Failed to load contract.');
      } finally {
        setLoading(false);
      }
    };
    if (contractId && token) load();
  }, [contractId, token]);

  const services = useMemo(() => {
    return Array.isArray(contract?.services) ? contract.services : [];
  }, [contract]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" fontWeight="bold">{contract.title}</Typography>
          <Chip size="small" label={contract.status || 'Draft'} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {client?.clientName ? `Client: ${client.clientName}` : 'Client'}
        </Typography>
        {billingContactEmail && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Billing contact email: {billingContactEmail}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>Scope</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {(contract.notes || '').trim() || '—'}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>Services</Typography>
        {services.length === 0 ? (
          <Alert severity="info">No services in this contract.</Alert>
        ) : (
          <Grid container spacing={2}>
            {services.map((svc) => (
              <Grid item xs={12} key={svc.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">{svc.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(String(svc.frequency || '').toLowerCase().includes('month') ? 'Monthly' : 'One-time')} • Qty {svc.quantity || 1}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" color="primary" fontWeight="bold">
                        {formatCurrency((Number(svc.unitPrice)||0) * (Number(svc.quantity)||1))}
                      </Typography>
                    </Box>
                    {svc.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {svc.description}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="h6" fontWeight="bold">
            Total: {formatCurrency(contract.totalAmount || 0)}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}


